import axios from 'axios';

// Function to get auth token from cookie
const getAuthTokenFromCookie = () => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; auth_token=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Configure axios defaults globally
axios.defaults.withCredentials = true; // Always include cookies in all requests

// Add a request interceptor to include the token from cookies in all requests
axios.interceptors.request.use(
  (config) => {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }

    // Get token from cookie first
  // Do not set Authorization header; rely on cookies only

    // Add these headers to ensure consistent behavior
    config.withCredentials = true; // Ensure cookies are sent with every request
    config.headers['Cache-Control'] = 'no-cache';
    
    // Only set Accept header to application/json if it's not already set and not a blob response
    if (!config.headers['Accept'] && config.responseType !== 'blob') {
      config.headers['Accept'] = 'application/json';
    }
    
    // Log export-related and GetStates requests for debugging
    if (config.url && (config.url.includes('Export') || config.url.includes('GetStates'))) {
      console.log(`Request to ${config.url}:`, {
        hasAuthHeader: !!config.headers.Authorization,
        withCredentials: config.withCredentials,
        contentType: config.headers['Content-Type'],
        accept: config.headers['Accept'],
        responseType: config.responseType,
        params: config.params
      });
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication errors
axios.interceptors.response.use(
  response => response,
  async error => {
    // Global error debug log
    console.error('AXIOS ERROR:', {
      url: error?.config?.url,
      status: error?.response?.status,
      method: error?.config?.method,
      data: error?.config?.data,
      responseData: error?.response?.data,
      isAxiosError: error.isAxiosError,
      message: error.message
    });

    // If the response is a blob type and it failed, we need to parse the blob to get the error message
    if (error.response && error.response.data instanceof Blob && error.response.data.type !== 'application/json') {
      // Only do this for non-JSON blobs that might contain error info
      try {
        // Convert blob to text
        const text = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsText(error.response.data);
        });
        
        // Try to parse as JSON
        try {
          const json = JSON.parse(text);
          error.response.data = json;
          
          // If we successfully parsed JSON, log it
          console.error('Error in blob response:', json);
        } catch (jsonError) {
          // If not valid JSON, use text as error message
          error.response.data = { message: text };
          console.error('Text error in blob response:', text);
        }
      } catch (blobError) {
        console.error('Error parsing blob error response:', blobError);
      }
    }
    

    // If 401 Unauthorized response, try to refresh token and retry request ONCE, with single-flight logic
    // Check for 401 in HTTP status or in response headers (as string or number)
    let is401 = false;
    if (error.response) {
      if (error.response.status === 401) {
        is401 = true;
      } else if (error.response.headers) {
        // Check common header keys for status
        const headerStatus = error.response.headers['status'] || error.response.headers['Status'] || error.response.headers['Status Code'] || error.response.headers['X-Status'];
        if (headerStatus && (headerStatus === '401' || headerStatus === 401)) {
          is401 = true;
        }
      }
    }
    if (is401) {
      // If already retried, do not retry again
      if (error.config._retry) {
        // Log cookie and token status for debugging
        console.error('Authentication error:', error.config.url);
        console.log('Auth cookie exists:', !!getAuthTokenFromCookie());
        console.log('Session token exists:', !!sessionStorage.getItem('authToken'));
        return Promise.reject(error);
      }
      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          // Do not set Authorization header; rely on cookies only
          error.config._retry = true;
          return axios(error.config);
        });
      }
      // Start refresh
      error.config._retry = true;
      isRefreshing = true;
      const oldToken = getAuthTokenFromCookie() || sessionStorage.getItem('authToken');
      refreshPromise = new Promise((resolve, reject) => {
        // Async logic handled with .then/.catch
        let userName = null;
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
          try {
            const userObj = JSON.parse(userStr);
            userName = userObj.userName || userObj.UserName || null;
          } catch (e) {
            console.warn('Could not parse user from sessionStorage:', e);
          }
        }
        axios.post('/api/Samlauth/refresh', null, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            ...(oldToken ? { 'Authorization': `Bearer ${oldToken}` } : {}),
            ...(userName ? { 'userName': userName } : {})
          },
        })
        .then(() => {
          // Get new token from cookie only (do not check sessionStorage)
          const newToken = getAuthTokenFromCookie();
          processQueue(null, newToken);
          resolve(newToken);
        })
        .catch((refreshError) => {
          processQueue(refreshError, null);
          reject(refreshError);
        })
        .finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      });
      return refreshPromise.then(() => {
        // Do not set Authorization header; rely on cookies only
        return axios(error.config);
      });
    }
    
    // For export-related and GetStates URLs, add more detailed logging

    if (error.config && error.config.url && (error.config.url.includes('Export') || error.config.url.includes('GetStates'))) {
      console.error(`API error (${error.response?.status || 'unknown status'}) for ${error.config.url}`, {
        url: error.config.url,
        method: error.config.method,
        hasAuthHeader: !!error.config.headers?.Authorization,
        params: error.config.params,
        hasErrorData: !!error.response?.data,
        errorMessage: error.message,
      });
    }
    return Promise.reject(error);
  }
);

// --- Single-flight refresh logic ---
let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

export default axios;
